import type { AuditLog } from '../types/api';

/**
 * Get translated target name
 */
export function getTranslatedTarget(target: string, t: (key: string) => string): string {
  const translations: Record<string, string> = {
    balance: t('admin.auditLogs.targets.balance') || 'الرصيد',
    user: t('admin.auditLogs.targets.user') || 'المستخدم',
    order: t('admin.auditLogs.targets.order') || 'الطلب',
    provider_config: t('admin.auditLogs.targets.providerConfig') || 'إعدادات المزود',
    settings: t('admin.auditLogs.targets.settings') || 'الإعدادات',
  };

  return translations[target] || target;
}

/**
 * Get translated action name
 */
export function getTranslatedAction(action: string, t: (key: string) => string): string {
  const translations: Record<string, string> = {
    recharge: t('admin.auditLogs.actions.recharge') || 'شحن الرصيد',
    user_balance_update: t('admin.auditLogs.actions.userBalanceUpdate') || 'تحديث رصيد المستخدم',
    user_ban_status_update: t('admin.auditLogs.actions.userBanStatusUpdate') || 'تحديث حالة الحظر',
    user_admin_status_update: t('admin.auditLogs.actions.userAdminStatusUpdate') || 'تحديث حالة المدير',
    purchase: t('admin.auditLogs.actions.purchase') || 'شراء',
    order_status_update: t('admin.auditLogs.actions.orderStatusUpdate') || 'تحديث حالة الطلب',
    order_refund: t('admin.auditLogs.actions.orderRefund') || 'استرداد الطلب',
    order_refill: t('admin.auditLogs.actions.orderRefill') || 'تعبئة الطلب',
    provider_config_create: t('admin.auditLogs.actions.providerConfigCreate') || 'إنشاء إعدادات المزود',
    provider_config_update: t('admin.auditLogs.actions.providerConfigUpdate') || 'تحديث إعدادات المزود',
    provider_config_delete: t('admin.auditLogs.actions.providerConfigDelete') || 'حذف إعدادات المزود',
    settings_update: t('admin.auditLogs.actions.settingsUpdate') || 'تحديث الإعدادات',
  };

  return translations[action] || action;
}

/**
 * Get all possible target values
 */
export function getTargetOptions(t: (key: string) => string): Array<{ value: string; label: string }> {
  return [
    { value: 'balance', label: t('admin.auditLogs.targets.balance') || 'الرصيد' },
    { value: 'user', label: t('admin.auditLogs.targets.user') || 'المستخدم' },
    { value: 'order', label: t('admin.auditLogs.targets.order') || 'الطلب' },
    { value: 'provider_config', label: t('admin.auditLogs.targets.providerConfig') || 'إعدادات المزود' },
    { value: 'settings', label: t('admin.auditLogs.targets.settings') || 'الإعدادات' },
  ];
}

/**
 * Get all possible action values for a specific target
 */
export function getActionOptionsForTarget(
  target: string,
  t: (key: string) => string
): Array<{ value: string; label: string }> {
  const actionMap: Record<string, Array<{ value: string; label: string }>> = {
    balance: [
      { value: 'recharge', label: t('admin.auditLogs.actions.recharge') || 'شحن الرصيد' },
    ],
    user: [
      { value: 'user_balance_update', label: t('admin.auditLogs.actions.userBalanceUpdate') || 'تحديث رصيد المستخدم' },
      { value: 'user_ban_status_update', label: t('admin.auditLogs.actions.userBanStatusUpdate') || 'تحديث حالة الحظر' },
      { value: 'user_admin_status_update', label: t('admin.auditLogs.actions.userAdminStatusUpdate') || 'تحديث حالة المدير' },
    ],
    order: [
      { value: 'purchase', label: t('admin.auditLogs.actions.purchase') || 'شراء' },
      { value: 'order_status_update', label: t('admin.auditLogs.actions.orderStatusUpdate') || 'تحديث حالة الطلب' },
      { value: 'order_refund', label: t('admin.auditLogs.actions.orderRefund') || 'استرداد الطلب' },
      { value: 'order_refill', label: t('admin.auditLogs.actions.orderRefill') || 'تعبئة الطلب' },
    ],
    provider_config: [
      { value: 'provider_config_create', label: t('admin.auditLogs.actions.providerConfigCreate') || 'إنشاء إعدادات المزود' },
      { value: 'provider_config_update', label: t('admin.auditLogs.actions.providerConfigUpdate') || 'تحديث إعدادات المزود' },
      { value: 'provider_config_delete', label: t('admin.auditLogs.actions.providerConfigDelete') || 'حذف إعدادات المزود' },
    ],
    settings: [
      { value: 'settings_update', label: t('admin.auditLogs.actions.settingsUpdate') || 'تحديث الإعدادات' },
    ],
  };

  return actionMap[target] || [];
}

